import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import ReactQuill from 'react-quill';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusOutlined, EditOutlined, DeleteOutlined, CloseOutlined, SaveOutlined } from '@ant-design/icons';
import { Modal, message } from 'antd';
import 'react-quill/dist/quill.snow.css';

const { confirm } = Modal;

const ServiceManager = () => {
  const [services, setServices] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [newService, setNewService] = useState({ title: '', summary: '' });
  const [isAdding, setIsAdding] = useState(false);
  const [errors, setErrors] = useState({ title: false, summary: false });
  const [charCount, setCharCount] = useState(0);
  const [expandedServices, setExpandedServices] = useState({});
  const quillRef = useRef(null);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/services`);
      setServices(res.data);
      // Initialize expanded state for each service
      const initialExpanded = {};
      res.data.forEach(service => {
        initialExpanded[service.id] = false;
      });
      setExpandedServices(initialExpanded);
    } catch (error) {
      message.error('Failed to fetch services');
    }
  };

  const toggleExpand = (id) => {
    setExpandedServices(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleInputChange = (value, field, id = null) => {
    if (field === 'summary') {
      const plainText = value.replace(/<[^>]*>/g, '');
      setCharCount(plainText.length);
      
      if (plainText.length > 200) {
        setErrors(prev => ({ ...prev, summary: true }));
      } else {
        setErrors(prev => ({ ...prev, summary: false }));
      }
    }

    if (id !== null) {
      setServices(prev =>
        prev.map(s => (s.id === id ? { ...s, [field]: value } : s))
      );
    } else {
      setNewService(prev => ({ ...prev, [field]: value }));
    }
  };

  const saveEdit = async (id) => {
    try {
      const service = services.find(s => s.id === id);
      
      await axios.put(`${process.env.REACT_APP_API_URL}/api/services/${id}`, {
        title: service.title,
        summary: service.summary,
      });
      setEditingId(null);
      message.success('Service updated successfully');
    } catch (error) {
      message.error('Failed to update service');
    }
  };

  const addService = async () => {
    const newErrors = {
      title: !newService.title.trim(),
      summary: !newService.summary.trim() 
    };
    
    setErrors(newErrors);
    
    if (newErrors.title || newErrors.summary) {
      message.warning('Please fill all required fields correctly');
      return;
    }

    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/services`, newService);
      setServices([...services, res.data]);
      setNewService({ title: '', summary: '' });
      setCharCount(0);
      setIsAdding(false);
      message.success('Service added successfully');
    } catch (error) {
      message.error('Failed to add service');
    }
  };

  const confirmDelete = (id) => {
    confirm({
      title: 'Delete Service',
      content: 'Are you sure you want to permanently delete this service? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      centered: true,
      maskClosable: true,
      onOk: async () => {
        try {
          await axios.delete(`${process.env.REACT_APP_API_URL}/api/services/${id}`);
          setServices(services.filter(s => s.id !== id));
          message.success('Service deleted successfully');
        } catch (error) {
          message.error('Failed to delete service');
        }
      },
    });
  };

  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'header': [1, 2, 3, false] }],
      [{ 'align': [] }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      ['link'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike', 
    'align', 'list', 'indent', 'link'
  ];

  useEffect(() => {
    if ((isAdding || editingId !== null) && quillRef.current) {
      setTimeout(() => {
        const quill = quillRef.current.getEditor();
        quill.focus();
      }, 300);
    }
  }, [isAdding, editingId]);

  return (
    <div className="max-w-5xl mx-auto p-6 min-h-screen">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-12 text-center"
      >
        <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
          Service Management
        </h1>
        <p className="text-gray-500">Create and manage your organization's services</p>
      </motion.div>

      <div className="flex justify-end mb-8">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl flex items-center gap-2 shadow-lg hover:shadow-xl transition-all"
          onClick={() => {
            setIsAdding(!isAdding);
            setErrors({ title: false, summary: false });
            setCharCount(0);
          }}
        >
          <PlusOutlined className="text-lg" />
          {isAdding ? 'Cancel' : 'Add Service'}
        </motion.button>
      </div>

      <AnimatePresence>
        {isAdding && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-white rounded-2xl shadow-xl p-6 mb-8 border border-gray-100"
          >
            <h3 className="text-xl font-semibold mb-6 text-gray-800">Create New Service</h3>
            <div className="space-y-6">
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700 flex items-center">
                  Title <span className="text-red-500 ml-1">*</span>
                  {errors.title && (
                    <span className="text-red-500 text-xs ml-2">Title is required</span>
                  )}
                </label>
                <input
                  type="text"
                  placeholder="Enter service title"
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.title ? 'border-red-500' : 'border-gray-200'
                  } focus:ring-2 focus:outline-none focus:ring-blue-500 focus:border-transparent`}
                  value={newService.title}
                  onChange={(e) => {
                    handleInputChange(e.target.value, 'title');
                    setErrors(prev => ({ ...prev, title: false }));
                  }}
                />
              </div>
              
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700 flex items-center">
                  Description <span className="text-red-500 ml-1">*</span>
                </label>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={newService.summary}
                    onChange={(value) => handleInputChange(value, 'summary')}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Enter detailed service description..."
                    className="quill-editor"
                  />
                </div>
                {errors.summary && (
                  <span className="text-red-500 text-xs">Description is required</span>
                )}
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-6 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                  onClick={() => {
                    setIsAdding(false);
                    setErrors({ title: false, summary: false });
                  }}
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                  onClick={addService}
                >
                  <SaveOutlined className="mr-2" />
                  Save Service
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid gap-6">
        {services.length === 0 && !isAdding ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white rounded-2xl shadow-sm p-8 text-center"
          >
            <div className="text-gray-400 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h3 className="text-xl font-medium text-gray-500 mb-2">No services found</h3>
            <p className="text-gray-400 mb-4">Get started by adding your first service</p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2.5 rounded-lg"
              onClick={() => setIsAdding(true)}
            >
              <PlusOutlined className="mr-2" />
              Add Service
            </motion.button>
          </motion.div>
        ) : (
          <AnimatePresence>
            {services.map(service => {
              const plainText = service.summary.replace(/<[^>]*>/g, '');
              const charCount = plainText.length;
              const isExpanded = expandedServices[service.id] || false;
              const shouldTruncate = charCount > 200;
              const displayText = isExpanded 
                ? service.summary 
                : (shouldTruncate 
                    ? `${service.summary.substring(0, 200)}...` 
                    : service.summary);

              return (
                <motion.div
                  key={service.id}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow p-6 border border-gray-100"
                >
                  {editingId === service.id ? (
                    <div className="space-y-6">
                      <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-700 flex items-center">
                          Title <span className="text-red-500 ml-1">*</span>
                        </label>
                        <input
                          type="text"
                          value={service.title}
                          className="w-full px-4 py-3 text-lg font-semibold border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          onChange={(e) => handleInputChange(e.target.value, 'title', service.id)}
                        />
                      </div>
                      
                      <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-700 flex items-center">
                          Description <span className="text-red-500 ml-1">*</span>
                        </label>
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                          <ReactQuill
                            theme="snow"
                            value={service.summary}
                            onChange={(value) => handleInputChange(value, 'summary', service.id)}
                            modules={quillModules}
                            formats={quillFormats}
                            className="quill-editor"
                          />
                        </div>
                      </div>
                      
                      <div className="flex justify-end gap-3 mt-4">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors flex items-center"
                          onClick={() => setEditingId(null)}
                        >
                          <CloseOutlined className="mr-2" />
                          Cancel
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                          onClick={() => saveEdit(service.id)}
                        >
                          <SaveOutlined className="mr-2" />
                          Save Changes
                        </motion.button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex justify-between items-start">
                        <h3 className="text-xl font-semibold text-gray-800">{service.title}</h3>
                        <div className="flex gap-2">
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="px-3 py-1 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors flex items-center text-sm"
                            onClick={() => setEditingId(service.id)}
                          >
                            <EditOutlined className="mr-1" />
                            Edit
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="px-3 py-1 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors flex items-center text-sm"
                            onClick={() => confirmDelete(service.id)}
                          >
                            <DeleteOutlined className="mr-1" />
                            Delete
                          </motion.button>
                        </div>
                      </div>
                      
                      <div
                        className="text-gray-600 leading-relaxed prose max-w-none"
                        dangerouslySetInnerHTML={{ __html: displayText }}
                      />
                      
                      {shouldTruncate && (
                        <button
                          onClick={() => toggleExpand(service.id)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium mt-2"
                        >
                          {isExpanded ? 'Read Less' : 'Read More'}
                        </button>
                      )}
                      
                      <div className="text-right text-xs text-gray-400 mt-2">
                        {charCount} characters
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
        )}
      </div>
      
      <style jsx global>{`
        .quill-editor .ql-container {
          min-height: 200px;
          font-size: 16px;
          line-height: 1.6;
        }
        .quill-editor .ql-editor {
          min-height: 200px;
        }
        .quill-editor .ql-toolbar {
          border-radius: 4px 4px 0 0;
          border: 1px solid #e2e8f0;
          border-bottom: none;
        }
        .quill-editor .ql-container {
          border-radius: 0 0 4px 4px;
          border: 1px solid #e2e8f0;
        }
        .quill-editor .ql-container.ql-snow {
          border-top: none;
        }
        .prose img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 10px 0;
        }
      `}</style>
    </div>
  );
};

export default ServiceManager;